/**
 * RunView — канва игрока run-вопроса «Беги к углу» (kind="run", Фаза 14, эпик
 * B1/B2; макет §3.4 спеки phase-14-interface-spec; РЕДИЗАЙН 0.14.1, решение
 * владельца 2026-10-20).
 *
 * Плитка с зонами 25%×25% в углах (0.25/0.75 — зеркально серверной геометрии
 * RunZone), тексты вариантов прижаты к ВНЕШНИМ углам плитки, пиксельный
 * человечек с руками/ногами (4 кадра ходьбы, поворот по направлению, squash
 * на старте/остановке — runSprites.tsx), дроны-квадрокоптеры (винты, тень,
 * след, hover-боббинг).
 *
 * Смерть = выбывание (0.14.1): попадание дрона клиент детектит ИДЕНТИЧНОЙ
 * серверу симуляцией (droneHitAt — круг-круг 0.065², порт game.RunDroneHit)
 * → мгновенный взрыв (вспышка/осколки/кольцо/дым + встряска канвы, ~0.75 с,
 * осколки детерминированы diedAtMs) → красное пятно до конца вопроса,
 * человечек исчезает, управление отключено, плашка «Погиб — вы наблюдатель»,
 * pos больше НЕ шлётся. Reconnect с kindData.dead — сразу наблюдатель + пятно
 * на МЕСТЕ гибели (персональные kindData.deadX/deadY, 0.14.1; фолбэк при
 * отсутствии — точка переподключения). Стан (run.stun, мигание) УДАЛЁН —
 * заменён смертью.
 *
 * Ввод — ЕДИНАЯ абстракция «вектор направления» (решение Е §7.2). Физика —
 * client-authoritative (план §1): симуляция локальна, серверу только pos
 * (~3 Гц, при изменении, видимой вкладке, до reveal/гибели). RunView
 * НИКОГДА не шлёт классический answer — зачёт полностью серверный.
 *
 * Рендер — DOM (без canvas API — jsdom-тесты без моков); rAF-луп мутирует
 * style/data-атрибуты напрямую (без ре-рендеров 60 fps), React-state —
 * только дискретные вещи (зона/смерть/вердикт). Анимации — CSS keyframes
 * c transform/opacity (GPU-friendly); prefers-reduced-motion (системный
 * или data-a11y-motion="reduce") отключает шейк/частицы/винты/боббинг —
 * остаются мгновенные состояния (пятно появляется сразу).
 *
 * 0.14.3: S1 — симуляция дронов в СЕРВЕРНОМ времени (kindData.startedAtMs/
 * now → offset; makeRunClock в runEngine) — фазы у ведущего и игрока
 * совпадают; S4 — серверный dead-пуш run_dead взрывает мгновенно (идемпотентно
 * локальному детекту, first-wins); S5 — при ЛОКАЛЬНОМ детекте гибели шлём
 * финальный pos (точку гибели) одним пакетом до отключки отправки; M1/M2 —
 * размеры спрайтов — доли плитки (--tile/--player-size/--drone-size): спрайт ≈
 * хитбокс (радиусы из params) на любом экране.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { isRunKindData, runCorners } from '../../protocol';
import { optionLabel } from '../ui-helpers';
import {
  RUN_PHYSICS,
  droneHitAt,
  dronePosAt,
  keyboardVector,
  joystickVector,
  makeRunClock,
  movePoint,
  runDifficulty,
  runZone,
  spawnDrones,
  type DroneSpec,
  type InputVector,
  type RunClock,
} from './runEngine';
import { prefersReducedMotion, useTilePx } from './runMotion';
import {
  DroneSpriteSvg,
  ExplosionLayers,
  PlayerSpriteSvg,
  SplatSvg,
  SPLAT_FADE_MS,
  SPLAT_TTL_MS,
} from './runSprites';
import type { QuestionBodyProps } from './types';

/** Период отправки pos: ~3 Гц (серверный rate-guard 10 Гц — запас ×3, §3.3). */
const POS_SEND_INTERVAL_MS = 333;
/** Эпсилон изменения позиции: меньше — считаем, что стоим на месте. */
const POS_EPS = 0.0005;
/** Стартовая позиция человечка: центр плитки (нейтральная зона, §3.2). */
const START_POS = { x: 0.5, y: 0.5 };
/** Плиток пути на кадр ходьбы: частота анимации пропорциональна скорости. */
const WALK_FRAME_DIST = 0.055;
/** Сглаживание поворота спрайта (доля нового угла в кадре, 0..1). */
const TURN_LERP = 0.35;

/** Визуальный коэффициент спрайта игрока к хитбоксу (M2: спрайт ≈ зона поражения). */
const PLAYER_SPRITE_K = 1.5;
/** Визуальный коэффициент спрайта дрона к хитбоксу (M2). */
const DRONE_SPRITE_K = 1.6;

/** Зафиксированная гибель игрока (дискретное состояние → React-рендер). */
interface DeathState {
  /** Момент гибели (мс от старта вопроса; детерминирует пятно/осколки). */
  ms: number;
  /** Место гибели (нормализованные координаты — центр взрыва/пятна). */
  x: number;
  y: number;
  /** true — reconnect-смерть (kindData.dead): взрыв не проигрываем. */
  reconnect: boolean;
}

export function RunView({ question, reveal, onPos, playerNick, runDead }: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const corners = runCorners(question);
  const kd = question.kindData;
  const seed = kd && isRunKindData(kd) && kd.seed !== undefined ? kd.seed : -1;
  const params = kd && isRunKindData(kd) && kd.params ? kd.params : RUN_PHYSICS;
  const revealed = reveal !== null;
  const kdDead = kd !== null && isRunKindData(kd) && kd.dead === true;
  const kdDiedAtMs =
    kdDead && kd !== null && isRunKindData(kd) && typeof kd.diedAtMs === 'number'
      ? kd.diedAtMs
      : 0;
  // Место гибели из персонального kindData (0.14.1): reconnect-пятно там, где
  // погиб, а не в точке переподключения. Фолбэк — старт (старые пакеты без полей).
  const kdDeadX =
    kdDead && kd !== null && isRunKindData(kd) && typeof kd.deadX === 'number'
      ? kd.deadX
      : START_POS.x;
  const kdDeadY =
    kdDead && kd !== null && isRunKindData(kd) && typeof kd.deadY === 'number'
      ? kd.deadY
      : START_POS.y;

  // ── 0.14.3 S1: серверные часы симуляции (kindData.startedAtMs/now → offset).
  // У игрока positions-пакетов нет — EMA-уточнения не будет, offset из kindData.now;
  // без полей (превью/старый пакет) — локальные часы (offset=0, база startedAt).
  const clock: RunClock = useMemo(
    () => makeRunClock(kd && isRunKindData(kd) ? kd : null, Date.now()),
    // Пересоздаётся на новый вопрос/kindData (reconnect с тем же вопросом —
    // свежий now; startedAtMs тот же — фаза симуляции не сдвигается).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question.questionId, kd],
  );

  // ── Локальное состояние симуляции (refs — без ре-рендеров на каждый кадр).
  const posRef = useRef({ ...START_POS });
  const heldKeysRef = useRef<Set<string>>(new Set());
  const stickRef = useRef<{ dx: number; dy: number } | null>(null);
  const lastZoneRef = useRef(-1);
  const lastSentRef = useRef({ x: START_POS.x, y: START_POS.y, at: 0 });
  const dronesRef = useRef<DroneSpec[]>([]);
  const tileRef = useRef<HTMLDivElement | null>(null);
  const playerElRef = useRef<HTMLDivElement | null>(null);
  const droneElsRef = useRef<Array<HTMLDivElement | null>>([]);
  const shadowElsRef = useRef<Array<HTMLDivElement | null>>([]);
  const stickElRef = useRef<HTMLDivElement | null>(null);
  const onPosRef = useRef(onPos);
  onPosRef.current = onPos;
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;

  // ── 0.14.3 M1/M2: px-плитка (useTilePx — после объявления tileRef) и
  // размеры спрайтов от хитбоксов params (спрайт ≈ зона поражения, M2).
  const tile = useTilePx(tileRef);
  const playerSize = 2 * params.playerRadius * tile * PLAYER_SPRITE_K;
  const droneSize = 2 * params.droneRadius * tile * DRONE_SPRITE_K;

  // Анимационные refs: кадр ходьбы, направление, squash-переходы.
  const walkDistRef = useRef(0);
  const headingRef = useRef(0);
  const movingRef = useRef(false);
  const deadRef = useRef<DeathState | null>(null);

  // Дискретное состояние для UI: зона, гибель, число дронов (после эффектного
  // заполнения dronesRef — ре-рендер, создающий DOM-узлы дронов/теней).
  const [zone, setZone] = useState<number>(-1);
  const [dead, setDead] = useState<DeathState | null>(null);
  const [droneCount, setDroneCount] = useState(0);
  // Пятно — исчезает по таймеру (0.14.2): SPLAT_TTL_MS → плавный fade
  // SPLAT_FADE_MS → удаление из DOM. Работает в ЛЮБОМ состоянии вопроса
  // (решение владельца главнее прежнего «до конца вопроса»); reduced-motion —
  // без перехода (CSS), удаление тем же таймером.
  const [splatFading, setSplatFading] = useState(false);
  const [splatGone, setSplatGone] = useState(false);
  useEffect(() => {
    setSplatFading(false);
    setSplatGone(false);
    if (!dead) return;
    const t1 = setTimeout(() => setSplatFading(true), SPLAT_TTL_MS);
    const t2 = setTimeout(() => setSplatGone(true), SPLAT_TTL_MS + SPLAT_FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [dead]);

  // Reconnect погибшего: kindData.dead — сразу наблюдатель + пятно на месте
  // гибели (deadX/deadY из персонального kindData, 0.14.1). Финальный pos НЕ
  // шлём: гибель зафиксирована сервером ещё до переподключения.
  useEffect(() => {
    if (kdDead) {
      const d: DeathState = {
        ms: kdDiedAtMs,
        x: kdDeadX,
        y: kdDeadY,
        reconnect: true,
      };
      deadRef.current = d;
      setDead(d);
    } else {
      deadRef.current = null;
      setDead(null);
    }
    // Сброс на новый вопрос (живой старт).
    posRef.current = { ...START_POS };
    walkDistRef.current = 0;
    headingRef.current = 0;
    lastZoneRef.current = -1;
    setZone(-1);
    // kdDead/kdDiedAtMs/kdDeadX/kdDeadY пересчитаны от kindData — входят в
    // deps: смена kindData-объекта (reconnect с dead) переигрывает инициализацию.
  }, [question.questionId, kdDead, kdDiedAtMs, kdDeadX, kdDeadY]);

  // Дроны вопроса: один список specs на вопрос (детерминизм seed); число —
  // в state, чтобы DOM-узлы появились сразу после спавна (не ждём внешний
  // рендер).
  useEffect(() => {
    dronesRef.current = spawnDrones(
      seed,
      params,
      runDifficulty(question.index, question.total),
      question.durationMs,
    );
    droneElsRef.current = [];
    shadowElsRef.current = [];
    setDroneCount(dronesRef.current.length);
  }, [question.questionId, seed, params, question.index, question.total, question.durationMs]);

  // ── Клавиатура (стрелки/WASD → held-множество → вектор в rAF).
  useEffect(() => {
    if (revealed || kdDead) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      const key = e.key.toLowerCase();
      if (key.startsWith('arrow')) e.preventDefault(); // не скроллим страницу.
      heldKeysRef.current.add(key);
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      heldKeysRef.current.delete(e.key.toLowerCase());
    };
    // Рассинхрон при потере фокуса с зажатой клавишей — сбрасываем.
    const onBlur = (): void => heldKeysRef.current.clear();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [revealed, kdDead]);

  // Гибель (0.14.1): взрыв + пятно + плашка; управление/pos отключаются.
  // 0.14.3 S5: finalPos — отправить ПОСЛЕДНИЙ pos с координатами точки гибели
  // (одноразово; die идемпотентен по deadRef — first-wins гарантирует один
  // пакет). true только при ЛОКАЛЬНОМ детекте (сервер ещё не знает точку);
  // при S4-run_dead сервер гибель уже зафиксировал — pos не нужен.
  function die(ms: number, x: number, y: number, finalPos = true): void {
    if (deadRef.current) return; // first-wins (локальный детект × run_dead).
    const d: DeathState = { ms, x, y, reconnect: false };
    deadRef.current = d;
    setDead(d);
    if (finalPos) onPosRef.current?.(x, y);
    // Встряска канвы ~250 мс (4–6 px): перезапуск keyframes тоглом класса.
    const tileEl = tileRef.current;
    if (tileEl && !prefersReducedMotion()) {
      tileEl.classList.remove('run-tile--shake');
      void tileEl.offsetWidth; // force reflow — перезапуск animation.
      tileEl.classList.add('run-tile--shake');
    }
  }

  // 0.14.3 S4: серверный dead-пуш run_dead — мгновенный взрыв + наблюдатель,
  // даже если локальный детект не сработал (фазовый сдвиг до S1). Пятно — по
  // x/y из события; идемпотентно локальному детекту (die first-wins), финальный
  // pos не шлём — сервер гибель уже зафиксировал этим же событием.
  useEffect(() => {
    if (!runDead) return;
    die(runDead.diedAtMs, runDead.x, runDead.y, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runDead]);

  // Squash при старте/остановке: короткое сплющивание СПРАЙТА (контейнер
  // занят rotate — анимация на вложенном svg; перезапуск тоглом класса,
  // reduced-motion — мгновенные состояния).
  function applyMoving(next: boolean): void {
    if (next === movingRef.current) return;
    movingRef.current = next;
    const el = playerElRef.current?.querySelector('.run-player__sprite');
    if (!el || prefersReducedMotion()) return;
    el.classList.remove('run-player__sprite--start', 'run-player__sprite--stop');
    void (el as SVGElement).getBoundingClientRect(); // force reflow.
    el.classList.add(next ? 'run-player__sprite--start' : 'run-player__sprite--stop');
  }

  // ── rAF-луп: физика, анимация, дроны, гибель, отправка pos. Останавливается
  // при reveal (заморозка до вердикта) и unmount; после гибели — продолжает
  // (дроны летают: игрок-наблюдатель смотрит вопрос до конца).
  useEffect(() => {
    if (revealed) return;
    let raf = 0;
    let last = performance.now();

    const applyZone = (next: number): void => {
      if (next !== lastZoneRef.current) {
        lastZoneRef.current = next;
        setZone(next);
      }
    };

    const frame = (now: number): void => {
      const dt = now - last;
      last = now;
      const deadNow = deadRef.current;
      // 0.14.3 S1: elapsed в СЕРВЕРНОМ времени (kindData.startedAtMs + offset
      // из kindData.now) — фаза дронов совпадает с ведущим и сервером; без
      // kindData-полей — локальные часы от question.startedAt, как до 0.14.3.
      const elapsed = clock.elapsedMs(Date.now(), question.startedAt);

      // Единая абстракция ввода: клавиши + джойстик → один вектор (решение Е).
      // Погиб = наблюдатель: ввод игнорируется (управление отключено).
      const kv = deadNow ? { x: 0, y: 0 } : keyboardVector(heldKeysRef.current);
      const stick = deadNow ? null : stickRef.current;
      const jv: InputVector = stick ? joystickVector(stick.dx, stick.dy, 48) : { x: 0, y: 0 };
      const v: InputVector = { x: kv.x + jv.x, y: kv.y + jv.y };
      const moving = !deadNow && (v.x !== 0 || v.y !== 0);
      applyMoving(moving);

      if (moving) {
        posRef.current = movePoint(posRef.current.x, posRef.current.y, v, dt, params);
        // Кадры ходьбы: частота от скорости (путь → фаза анимации).
        walkDistRef.current += Math.hypot(v.x, v.y) * params.moveSpeed * (dt / 1000);
        // Поворот по направлению движения: сглаживание к целевому углу
        // (спрайт «лицом» вверх → atan2 + 90°).
        const target = Math.atan2(v.y, v.x) * (180 / Math.PI) + 90;
        const delta = ((target - headingRef.current + 540) % 360) - 180;
        headingRef.current += delta * TURN_LERP;
      }

      // Дроны: локальная seed-симуляция (те же траектории у ведущего, B2):
      // позиция, курсовая ориентация, тень; + клиентский детект гибели
      // (идентичной серверу симуляцией — мгновенный взрыв, 0.14.1).
      const p = posRef.current;
      const dead2 = deadRef.current;
      for (let i = 0; i < dronesRef.current.length; i++) {
        const el = droneElsRef.current[i];
        const shadow = shadowElsRef.current[i];
        if (!el) continue;
        const spec = dronesRef.current[i];
        const at = dronePosAt(spec, elapsed);
        if (at === null) {
          el.style.display = 'none';
          if (shadow) shadow.style.display = 'none';
          continue;
        }
        el.style.display = '';
        el.style.left = `${at.x * 100}%`;
        el.style.top = `${at.y * 100}%`;
        el.style.transform = `translate(-50%, -50%) rotate(${(Math.atan2(spec.dy, spec.dx) * 180) / Math.PI + 90}deg)`;
        if (shadow) {
          shadow.style.display = '';
          shadow.style.left = `${at.x * 100}%`;
          shadow.style.top = `${at.y * 100 + 1.4}%`; // тень чуть «под» дроном.
        }
      }

      // Клиентский детект гибели — идентичной серверу симуляцией (0.14.1):
      // круг-круг 0.065² с каждым АКТИВНЫМ дроном (одна проверка на кадр).
      // die → финальный pos (S5, точка гибели) + отключка отправки.
      if (!dead2 && droneHitAt(dronesRef.current, params, p.x, p.y, elapsed)) {
        die(elapsed, p.x, p.y);
      }

      // Человечек: позиция, поворот, кадр ходьбы (idle — дыхание CSS).
      const playerEl = playerElRef.current;
      if (playerEl) {
        playerEl.style.left = `${p.x * 100}%`;
        playerEl.style.top = `${p.y * 100}%`;
        playerEl.style.transform = `translate(-50%, -50%) rotate(${headingRef.current}deg)`;
        playerEl.dataset.frame = moving
          ? String(Math.floor(walkDistRef.current / WALK_FRAME_DIST) % 4)
          : '1';
      }
      if (!deadRef.current) applyZone(runZone(p.x, p.y));

      // pos → сервер: ~3 Гц, только при изменении, только на видимой вкладке,
      // только пока ЖИВ (погибший больше не шлёт позиции, 0.14.1).
      const hidden = typeof document !== 'undefined' && document.hidden;
      const moved =
        Math.abs(p.x - lastSentRef.current.x) > POS_EPS ||
        Math.abs(p.y - lastSentRef.current.y) > POS_EPS;
      if (!deadRef.current && !hidden && moved && now - lastSentRef.current.at >= POS_SEND_INTERVAL_MS) {
        lastSentRef.current = { x: p.x, y: p.y, at: now };
        onPosRef.current?.(p.x, p.y);
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [revealed, question.questionId, question.startedAt, params, clock]);

  // ── Тач-джойстик (pointer events; мышь работает так же — бонус десктопа).
  function handleStickDown(e: React.PointerEvent<HTMLDivElement>): void {
    e.currentTarget.setPointerCapture(e.pointerId);
    updateStick(e);
  }
  function handleStickMove(e: React.PointerEvent<HTMLDivElement>): void {
    // Только активное касание/захват: stickRef задаётся в pointerdown.
    if (stickRef.current === null) return;
    updateStick(e);
  }
  function handleStickUp(): void {
    stickRef.current = null;
    const el = stickElRef.current;
    if (el) el.style.transform = 'translate(-50%, -50%)';
  }
  function updateStick(e: React.PointerEvent<HTMLDivElement>): void {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    stickRef.current = { dx, dy };
    const el = stickElRef.current;
    if (el) {
      const r = 48;
      const len = Math.hypot(dx, dy);
      const cl = len > r ? r / len : 1;
      el.style.transform = `translate(calc(-50% + ${dx * cl}px), calc(-50% + ${dy * cl}px))`;
    }
  }

  // ── Reveal-вердикт (§3.4): по последней зоне игрока и correctOption;
  // погибший (0.14.1) видит «Вы погибли» — зачёта нет независимо от угла.
  function verdict(): { text: string; ok: boolean | null } | null {
    if (!reveal) return null;
    const correct = reveal.correctOption;
    if (correct === undefined) return null;
    if (dead) return { text: t('run.reveal.dead'), ok: false };
    const z = lastZoneRef.current;
    if (z === correct)
      return { text: t('run.reveal.correct', { corner: cornerName(correct) }), ok: true };
    if (z >= 0) return { text: t('run.reveal.wrong', { corner: cornerName(z) }), ok: false };
    return { text: t('run.reveal.missed'), ok: null };
  }
  function cornerName(i: number): string {
    return corners[i] ?? t('run.zoneIndicator', { n: i + 1 });
  }

  const v = verdict();

  return (
    <div
      className="run-view"
      style={
        {
          // 0.14.3 M1/M2: плитка в px (механизм --ex из 0.14.1, обобщён) +
          // размеры спрайтов от хитбоксов params (спрайт ≈ зона поражения).
          '--tile': `${tile}px`,
          '--player-size': `${playerSize.toFixed(1)}px`,
          '--drone-size': `${droneSize.toFixed(1)}px`,
        } as CSSProperties
      }
    >
      <p className="muted small run-hint" data-testid="run-corner-hint">
        {t('run.cornerHint')}
      </p>
      <div
        className={revealed ? 'run-tile run-tile--frozen' : 'run-tile'}
        ref={tileRef}
        data-testid="run-canvas"
        role="application"
        aria-label={t('run.cornerHint')}
      >
        {/* Зоны зачёта 25%×25% строго по геометрии сервера (0.25/0.75, §3.2
            редизайн 0.14.1): рамка/заливка только по площади зачёта. */}
        {corners.map((_, i) => (
          <div
            key={`z-${i}`}
            className={
              'run-zone run-zone--' +
              i +
              (zone === i && !dead ? ' run-zone--active' : '') +
              (revealed && reveal?.correctOption === i ? ' run-zone--correct' : '')
            }
            data-testid={`run-zone-${i}`}
            aria-hidden="true"
          />
        ))}

        {/* Тексты вариантов — по дальним (внешним) углам плитки (0.14.1):
            ЛВ — влево-вверх, ПВ — вправо-вверх, ЛН — влево-вниз, ПН —
            вправо-вниз; не по левому верхнему углу ячейки. */}
        {corners.map((corner, i) => (
          <div key={`c-${i}`} className={'run-corner run-corner--' + i} data-testid={`run-corner-${i}`}>
            <span className="run-corner__label" data-testid={`run-corner-label-${i}`}>
              <span className="run-corner__index">{optionLabel(i)}</span>
              <span className="run-corner__text">{corner}</span>
            </span>
          </div>
        ))}

        {/* Нейтральные границы по геометрии сервера (0.25/0.75, §3.2). */}
        <div className="run-grid-line run-grid-line--v" style={{ left: '25%' }} aria-hidden="true" />
        <div className="run-grid-line run-grid-line--v" style={{ left: '75%' }} aria-hidden="true" />
        <div className="run-grid-line run-grid-line--h" style={{ top: '25%' }} aria-hidden="true" />
        <div className="run-grid-line run-grid-line--h" style={{ top: '75%' }} aria-hidden="true" />

        {/* Дроны-квадрокоптеры + тени (rAF: позиция/курс; винты/боббинг/след —
            CSS). Погибший продолжает видеть симуляцию (наблюдатель). */}
        {Array.from({ length: droneCount }, (_, i) => (
          <div key={`ds-${i}`} className="run-shadow" ref={(el) => { shadowElsRef.current[i] = el; }} style={{ display: 'none' }} aria-hidden="true" />
        ))}
        {Array.from({ length: droneCount }, (_, i) => (
          <div
            key={`d-${i}`}
            className="run-drone"
            ref={(el) => {
              droneElsRef.current[i] = el;
            }}
            style={{ display: 'none' }}
            aria-hidden="true"
          >
            <DroneSpriteSvg />
            <span className="run-drone__trail" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </div>
        ))}

        {/* Гибель (0.14.1): взрыв (~0.75 с; осколки/пятно детерминированы
            diedAtMs). 0.14.2: пятно исчезает по таймеру (~2.8 с + fade 0.6 с)
            независимо от фазы вопроса. Reconnect-смерть — пятно сразу, без
            взрыва (гибель была в прошлом). */}
        {dead && !splatGone ? (
          <div
            className={
              'run-splat' +
              (dead.reconnect ? ' run-splat--instant' : '') +
              (splatFading ? ' run-splat--fade' : '')
            }
            data-testid="run-splat"
            style={{ left: `${dead.x * 100}%`, top: `${dead.y * 100}%` }}
            aria-hidden="true"
          >
            <SplatSvg diedAtMs={dead.ms} />
          </div>
        ) : null}
        {dead && !dead.reconnect ? (
          <div
            className="run-explosion"
            data-testid="run-explosion"
            style={
              {
                left: `${dead.x * 100}%`,
                top: `${dead.y * 100}%`,
                '--ex': `${tile}px`,
              } as CSSProperties
            }
            aria-hidden="true"
          >
            <ExplosionLayers diedAtMs={dead.ms} />
          </div>
        ) : null}

        {/* Человечек (пиксельный, 4 кадра ходьбы, поворот, squash); погиб —
            исчезает (остаётся пятно). */}
        {!dead ? (
          <div
            className="run-player"
            ref={playerElRef}
            data-testid="run-player"
            data-frame="1"
            style={{ left: `${START_POS.x * 100}%`, top: `${START_POS.y * 100}%` }}
          >
            <PlayerSpriteSvg color="var(--accent)" />
            {playerNick ? <span className="run-player__nick">{playerNick}</span> : null}
          </div>
        ) : null}

        {/* Плашка наблюдателя (не блокирует обзор: pointer-events нет). */}
        {dead && !revealed ? (
          <div className="run-dead-overlay" data-testid="run-dead-overlay" role="status">
            <span className="run-dead-overlay__title">{t('run.dead')}</span>
            <span className="run-dead-overlay__hint">{t('run.deadHint')}</span>
          </div>
        ) : null}
      </div>

      <div className="run-status">
        <span
          className={zone >= 0 && !dead ? 'run-zonechip run-zonechip--in' : 'run-zonechip'}
          data-testid="run-zone-indicator"
          aria-live="polite"
        >
          {dead
            ? t('run.dead')
            : zone >= 0
              ? t('run.zoneIndicator', { n: zone + 1, corner: corners[zone] ?? '' })
              : t('run.neutralZone')}
        </span>
      </div>

      <p className="muted small run-controls run-controls--desktop">{t('run.controlsDesktop')}</p>
      <p className="muted small run-controls run-controls--touch">{t('run.controlsTouch')}</p>

      {/* Джойстик скрыт у погибшего: управление отключено (0.14.1). */}
      {!dead ? (
        <div
          className="run-joystick"
          data-testid="run-joystick"
          role="application"
          aria-label={t('run.controlsTouch')}
          onPointerDown={handleStickDown}
          onPointerMove={handleStickMove}
          onPointerUp={handleStickUp}
          onPointerCancel={handleStickUp}
        >
          <div className="run-joystick__stick" ref={stickElRef} aria-hidden="true" />
        </div>
      ) : null}

      {v ? (
        <p
          className={
            v.ok === true ? 'q-hint q-hint--ok' : v.ok === false ? 'q-hint q-hint--wrong' : 'q-hint'
          }
          data-testid="run-reveal-verdict"
        >
          {v.text}
        </p>
      ) : null}
    </div>
  );
}
